import { useId } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/cn";

export interface MultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value?: string[];
  onChange?: (value: string[]) => void;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  columns?: 1 | 2 | 3;
  id?: string;
  className?: string;
}

export function MultiSelect({
  options,
  value = [],
  onChange,
  label,
  error,
  required,
  disabled,
  columns = 2,
  id: idProp,
  className,
}: MultiSelectProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;

  function handleToggle(optValue: string) {
    if (disabled) return;
    const next = value.includes(optValue)
      ? value.filter((v) => v !== optValue)
      : [...value, optValue];
    onChange?.(next);
  }

  const colClass =
    columns === 3
      ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
      : columns === 2
        ? "grid-cols-1 sm:grid-cols-2"
        : "grid-cols-1";

  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <label className="text-sm font-medium leading-none text-foreground">
          {label}
          {required && <span className="ml-0.5 text-destructive">*</span>}
        </label>
      )}
      <div
        id={id}
        role="group"
        aria-label={label}
        aria-invalid={error ? "true" : undefined}
        className={cn("grid gap-2", colClass)}
      >
        {options.map((option) => {
          const checked = value.includes(option.value);
          return (
            <label
              key={option.value}
              className={cn(
                "flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors hover:bg-accent",
                checked && "border-primary bg-primary/5",
                (disabled || option.disabled) && "cursor-not-allowed opacity-50",
              )}
            >
              <div
                className={cn(
                  "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                  checked
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input",
                )}
              >
                {checked && <Check className="h-3 w-3" />}
              </div>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => handleToggle(option.value)}
                disabled={disabled || option.disabled}
                className="sr-only"
              />
              <span>{option.label}</span>
            </label>
          );
        })}
      </div>
      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}
    </div>
  );
}
